/**
 * Sewing Floor Board — the row board, as `<DataTable>` columns. See `design-notes.md`.
 *
 * Every colour comes from a category the server assigned (`efficiency_status`, `efficiency_band`,
 * `wft_status`, `wip_status`, `line_status`). This file maps a category to a token and does
 * nothing else — **no threshold lives here**; they are in `lines.sql` (§5.2).
 */

import type { ColumnDef } from '@tanstack/react-table';

import { cn } from '@/lib/utils';
import {
  efficiencyStatusTone,
  lineStatusLabel,
  lineStatusTone,
  wftStatusTone,
  wipStatusTone,
  type StatusTone,
} from '@/reports/sewing-line-status';

/** A row of the `lines` view. Values arrive already in their declared type (§5.7). */
export type BoardRow = Record<string, unknown>;

function num(row: BoardRow, field: string): number | null {
  const value = row[field];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(row: BoardRow, field: string): string {
  const value = row[field];
  return typeof value === 'string' ? value : '';
}

interface Scale {
  cell: string;
  header: string;
  lineId: string;
  style: string;
  ctq: string;
  track: string;
  effValue: string;
  tickLabel: string;
  pill: string;
  dot: string;
  wftValue: string;
  wftLimit: string;
  /** Column widths, mirroring the design's `250px 1fr 220px 170px` grid. Efficiency takes the slack via `w-full`. */
  wLine: string;
  wStatus: string;
  wWft: string;
  wWip: string;
  wipTrack: string;
  wipDays: string;
  wipPcs: string;
}

/**
 * The board's densities; `useFittedTier` measures the available height and picks one.
 * Discrete prebuilt class sets, never a computed font size — §4.8 allows runtime `style` for geometry only.
 */
export type Tier = 'desk' | 'sm' | 'md' | 'lg' | 'xl';

const SCALE: Record<Tier, Scale> = {
  // ~68px rows. The design's own figures, rounded to the scale.
  xl: {
    cell: 'py-4 px-5 align-middle',
    header: 'py-2 px-5 text-base font-semibold tracking-widest uppercase',
    lineId: 'text-4xl font-bold leading-none',
    style: 'text-lg',
    ctq: 'text-sm px-2 py-0.5',
    track: 'h-7 rounded-full',
    effValue: 'w-32 text-4xl font-bold',
    tickLabel: 'text-base',
    pill: 'text-base px-3 py-1.5 tracking-wide',
    dot: 'size-4',
    wftValue: 'text-3xl font-bold',
    wftLimit: 'w-20 text-base',
    wLine: 'min-w-72',
    wStatus: 'min-w-60',
    wWft: 'min-w-56',
    wWip: 'min-w-96',
    wipTrack: 'h-7 rounded-md',
    wipDays: 'w-16 text-2xl font-bold',
    wipPcs: 'w-24 text-base',
  },
  // ~40px rows — BVN K5-F3 (19 lines), BCA A/2 (10 on a busy day).
  lg: {
    cell: 'py-2 px-4 align-middle',
    header: 'py-1.5 px-4 text-sm font-semibold tracking-widest uppercase',
    lineId: 'text-2xl font-bold leading-none',
    style: 'text-base',
    ctq: 'text-xs px-2 py-0.5',
    track: 'h-6 rounded-full',
    effValue: 'w-28 text-2xl font-bold',
    tickLabel: 'text-sm',
    pill: 'text-sm px-2.5 py-1 tracking-wide',
    dot: 'size-3.5',
    wftValue: 'text-xl font-bold',
    wftLimit: 'w-16 text-sm',
    wLine: 'min-w-64',
    wStatus: 'min-w-56',
    wWft: 'min-w-52',
    wWip: 'min-w-80',
    wipTrack: 'h-6 rounded-md',
    wipDays: 'w-14 text-xl font-bold',
    wipPcs: 'w-20 text-sm',
  },
  // ~26px rows — PGV P3 Sewing (23 lines) and anything similar.
  md: {
    cell: 'py-1 px-4 align-middle',
    header: 'py-1 px-4 text-sm font-semibold tracking-widest uppercase',
    lineId: 'text-lg font-bold leading-none',
    style: 'text-sm',
    ctq: 'text-xs px-1.5 py-0.5',
    track: 'h-4 rounded-full',
    effValue: 'w-24 text-lg font-bold',
    tickLabel: 'text-xs',
    pill: 'text-sm px-2 py-0.5 tracking-wide',
    dot: 'size-3',
    wftValue: 'text-base font-bold',
    wftLimit: 'w-16 text-xs',
    wLine: 'min-w-56',
    wStatus: 'min-w-48',
    wWft: 'min-w-44',
    wWip: 'min-w-72',
    wipTrack: 'h-4 rounded-sm',
    wipDays: 'w-12 text-base font-bold',
    wipPcs: 'w-20 text-xs',
  },
  // ~20px rows — the floor this degrades to; below it a second screen is the answer, not smaller type.
  sm: {
    cell: 'py-0.5 px-3 align-middle',
    header: 'py-1 px-3 text-xs font-semibold tracking-widest uppercase',
    lineId: 'text-base font-bold leading-none',
    style: 'text-xs',
    ctq: 'text-xs px-1.5 py-0.5',
    track: 'h-3 rounded-full',
    effValue: 'w-20 text-base font-bold',
    tickLabel: 'text-xs',
    pill: 'text-xs px-2 py-0.5 tracking-wide',
    dot: 'size-2.5',
    wftValue: 'text-sm font-bold',
    wftLimit: 'w-14 text-xs',
    wLine: 'min-w-48',
    wStatus: 'min-w-40',
    wWft: 'min-w-40',
    wWip: 'min-w-64',
    wipTrack: 'h-3 rounded-sm',
    wipDays: 'w-12 text-sm font-bold',
    wipPcs: 'w-16 text-xs',
  },
  // At a desk the board is a report again, at report density.
  desk: {
    cell: 'py-2 px-3 align-middle',
    header: 'py-1.5 px-3 text-xs font-semibold tracking-widest uppercase',
    lineId: 'text-base font-bold leading-none',
    style: 'text-xs',
    ctq: 'text-xs px-1.5 py-0.5',
    track: 'h-5 rounded-full',
    effValue: 'w-16 text-base font-bold',
    tickLabel: 'text-xs',
    pill: 'text-xs px-2 py-0.5 tracking-wide',
    dot: 'size-2.5',
    wftValue: 'text-base font-bold',
    wftLimit: 'w-14 text-xs',
    wLine: 'min-w-44',
    wStatus: 'min-w-36',
    wWft: 'min-w-36',
    wWip: 'min-w-56',
    wipTrack: 'h-5 rounded-sm',
    wipDays: 'w-12 text-sm font-bold',
    wipPcs: 'w-16 text-xs',
  },
};

type Tone = StatusTone;

/** Same maps as the factory board — `sewing-line-status.ts`. */
function effTone(row: BoardRow): Tone {
  return efficiencyStatusTone(str(row, 'efficiency_status'));
}

function lineTone(row: BoardRow): Tone {
  return lineStatusTone(str(row, 'line_status'));
}

function wftTone(row: BoardRow): Tone {
  return wftStatusTone(str(row, 'wft_status'));
}

function wipTone(row: BoardRow): Tone {
  return wipStatusTone(str(row, 'wip_status'));
}

/** Display only — the API and the export still carry the full `product_type` including the fabric word. */
const FABRIC_PREFIX = /^(?:KNIT|WOVEN)\s+/i;

function styleLabel(value: string): string {
  const stripped = value.replace(FABRIC_PREFIX, '').trim();
  // A product_type that is only the fabric word keeps it — an empty cell would be worse.
  return stripped === '' ? value : stripped;
}

/** Bar geometry, clamped to the track. A line over 100% fills it rather than overflowing. */
function widthPct(value: number | null): string {
  return `${String(Math.max(0, Math.min(100, value ?? 0)))}%`;
}

/**
 * The efficiency bar, ticked with the line's own target — the floor target where it has none (§4.6).
 * Inline `style` here is §4.8's one exception and carries runtime geometry only; every colour is a token.
 */
function EfficiencyCell({ row, s }: { row: BoardRow; s: Scale }) {
  const efficiency = num(row, 'efficiency');
  const lineTarget = num(row, 'target_efficiency');
  const target = lineTarget ?? num(row, 'efficiency_target');
  const tone = effTone(row);

  return (
    <div className="flex items-center gap-4">
      <div className={cn('relative min-w-32 flex-1 bg-muted', s.track)}>
        <div
          className={cn('absolute inset-y-0 left-0', s.track, tone.fill)}
          style={{ width: widthPct(efficiency) }}
        />
        {target === null ? null : (
          <>
            <div
              className="absolute -inset-y-1 w-1 rounded-xs bg-foreground/70"
              style={{ left: widthPct(target) }}
              aria-hidden
            />
            {/* Flips left near the end of the track, and keeps its own plate: it can land on bare track or any of three fills. */}
            <span
              className={cn(
                'absolute top-1/2 -translate-y-1/2 rounded-xs bg-background/85 px-1',
                'font-medium whitespace-nowrap text-foreground/80',
                target >= 70 ? '-translate-x-full -ml-2' : 'ml-2',
                s.tickLabel,
              )}
              style={{ left: widthPct(target) }}
            >
              {target.toFixed(0)}%{lineTarget === null ? ' floor' : ''}
            </span>
          </>
        )}
      </div>
      <span className={cn('shrink-0 text-right tabular-nums', s.effValue, tone.text)}>
        {efficiency === null ? '—' : `${efficiency.toFixed(1)}%`}
      </span>
    </div>
  );
}

/**
 * The approved cover band, as geometry. These two are duplicated from `lines.sql`'s `wip_status`,
 * but only to place the shaded rectangle — the COLOUR still comes from the server's category, so if
 * the band moves in SQL the fill follows it and only this rectangle needs updating. Same two numbers
 * the KPI dashboard hardcodes (sftsEinsKpi.js:58-68).
 */
const WIP_OK_MIN_DAYS = 1;
const WIP_OK_MAX_DAYS = 3;
/** Track length: one day past the top of the band, so an EXCESS line still shows headroom. */
const WIP_SCALE_DAYS = WIP_OK_MAX_DAYS + 1;

function WipCell({ row, s }: { row: BoardRow; s: Scale }) {
  const days = num(row, 'wip_cover_days');
  const pcs = num(row, 'wip_pcs');
  // Not gated on is_running: a line with WIP but no plan today has pieces and a null cover, and
  // showing the pieces beside a dash is real information the placeholder could not express.
  if (days === null && pcs === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const tone = wipTone(row);
  const width = `${String(Math.min(100, ((days ?? 0) / WIP_SCALE_DAYS) * 100))}%`;

  return (
    <div className="flex items-center gap-3">
      <div className={cn('relative min-w-16 flex-1 overflow-hidden bg-muted', s.wipTrack)}>
        {/* The healthy band, expressed from the same constants as the fill so the two cannot drift apart. */}
        <div
          className="absolute inset-y-0 bg-status-ok/20"
          style={{
            left: `${String((WIP_OK_MIN_DAYS / WIP_SCALE_DAYS) * 100)}%`,
            width: `${String(((WIP_OK_MAX_DAYS - WIP_OK_MIN_DAYS) / WIP_SCALE_DAYS) * 100)}%`,
          }}
          aria-hidden
        />
        <div className={cn('absolute inset-y-0 left-0', tone.fill)} style={{ width }} />
      </div>
      <span className={cn('shrink-0 text-right tabular-nums', s.wipDays, tone.text)}>
        {days === null ? '—' : `${days.toFixed(1)}d`}
      </span>
      <span className={cn('shrink-0 text-right text-muted-foreground tabular-nums', s.wipPcs)}>
        {pcs === null ? '' : `${pcs.toLocaleString()} pcs`}
      </span>
    </div>
  );
}

/**
 * The board's columns — authored, not derived from the envelope; the rest of the response feeds the
 * export and the headline cards. `enableSorting` follows the server's `sort:` whitelist (§4.4, §4.6)
 * so a header only offers an order the export can repeat; the ordering itself is the browser's.
 */
export function boardColumns(tier: Tier): ColumnDef<BoardRow>[] {
  const s = SCALE[tier];
  const meta = (
    width: string,
    extra?: Partial<{ align: 'left' | 'right'; headerAlign: 'left' | 'right'; exportLabel: string }>,
  ) => ({
    cellClassName: cn(s.cell, width),
    headerClassName: cn(s.header, width, 'text-muted-foreground'),
    ...extra,
  });

  return [
    {
      id: 'line_label',
      accessorFn: (row) => str(row, 'line_label'),
      header: 'Line',
      enableSorting: true,
      meta: meta(s.wLine, { exportLabel: 'Line' }),
      cell: ({ row }) => {
        const line = row.original;
        const running = line['is_running'] === true;
        return (
          <div className="flex items-baseline gap-3 whitespace-nowrap">
            {/* Tinted by the composite lineTone, not the bar's effTone — a quality or WIP problem reddens the code while the pace-only bar stays green; see design-notes.md. */}
            <span className={cn(s.lineId, lineTone(line).text)}>{str(line, 'line_label')}</span>
            {line['has_ctq'] === true ? (
              <span
                className={cn(
                  'shrink-0 rounded-sm bg-ctq font-bold tracking-wide text-ctq-foreground uppercase',
                  s.ctq,
                )}
                title="Critical to quality — this line is running at least one CTQ order today"
              >
                CTQ
              </span>
            ) : null}
            <span className={cn(s.style, 'text-muted-foreground')}>
              {running ? styleLabel(str(line, 'product_type')) : 'Not running'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'efficiency',
      accessorFn: (row) => num(row, 'efficiency'),
      header: 'Efficiency vs target',
      enableSorting: true,
      // w-full, so this column absorbs every pixel the other three do not claim.
      meta: meta('w-full', { exportLabel: 'Efficiency' }),
      cell: ({ row }) => <EfficiencyCell row={row.original} s={s} />,
    },
    {
      id: 'line_status',
      accessorFn: (row) => str(row, 'line_status'),
      header: 'Status',
      meta: meta(s.wStatus, { exportLabel: 'Status' }),
      cell: ({ row }) => {
        const status = str(row.original, 'line_status');
        return (
          <span
            className={cn(
              'inline-block rounded-md font-semibold whitespace-nowrap',
              s.pill,
              lineTone(row.original).wash,
            )}
          >
            {lineStatusLabel(status)}
          </span>
        );
      },
    },
    {
      id: 'wft',
      accessorFn: (row) => num(row, 'wft'),
      header: 'WFT',
      enableSorting: true,
      // Cells right-align so the figures stack; the heading stays left, beside the column it names.
      meta: meta(s.wWft, { align: 'right', headerAlign: 'left', exportLabel: 'WFT' }),
      cell: ({ row }) => {
        const line = row.original;
        const wft = num(line, 'wft');
        const limit = num(line, 'wft_limit');
        const tone = wftTone(line);
        return (
          <div className="flex items-center justify-end gap-3 whitespace-nowrap">
            <span className={cn('shrink-0 rounded-full', s.dot, tone.fill)} aria-hidden />
            <span className={cn('tabular-nums', s.wftValue, tone.text)}>
              {wft === null ? '—' : `${wft.toFixed(2)}%`}
            </span>
            <span className={cn('text-muted-foreground', s.wftLimit)}>
              {limit === null ? '' : `/ ${limit.toFixed(1)}%`}
            </span>
          </div>
        );
      },
    },
    {
      // `id` must be the field name: it becomes the URL's sort field and the export's `field`, and
      // the export resolves it against the view's declared columns — 'wip_cover' 400s there.
      id: 'wip_cover_days',
      accessorFn: (row) => num(row, 'wip_cover_days'),
      header: 'WIP cover',
      enableSorting: true,
      // One rendered column exports one field, so the file carries days while wip_pcs/wip_status stay
      // in the response — the WFT column's precedent exactly.
      meta: meta(s.wWip, { exportLabel: 'WIP cover (days)' }),
      cell: ({ row }) => <WipCell row={row.original} s={s} />,
    },
  ];
}

/** The footer legend — it exists to explain the vertical tick, the one mark that is not self-describing. */
export function BoardLegend({ tier }: { tier: Tier }) {
  return (
    <p
      className={cn(
        'flex flex-none flex-wrap items-center gap-x-8 gap-y-1 text-muted-foreground',
        tier === 'xl' && 'px-5 py-2.5 text-base',
        tier === 'lg' && 'px-4 py-2 text-sm',
        (tier === 'md' || tier === 'sm') && 'px-4 py-1.5 text-xs',
        tier === 'desk' && 'px-1 py-2 text-xs',
      )}
    >
      <span>Vertical tick = the line&rsquo;s own target for today</span>
      <span>Green over 95% of target &middot; amber 70&ndash;95% &middot; red under 70%</span>
      <span>Marked &ldquo;floor&rdquo; where the line has no plan, and the factory target is used</span>
      <span>Green band = healthy WIP cover, 1&ndash;3 days</span>
      <span className="text-ctq">CTQ = critical to quality</span>
      <span>Status names the first thing to fix: low WIP, then high WFT, then efficiency</span>
    </p>
  );
}
